import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';

import { AuthenticatedMobileShell } from '../components/AuthenticatedMobileShell.tsx';
import { NumberField } from '../components/NumberField.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { TextArea } from '../components/TextArea.tsx';
import { TextField } from '../components/TextField.tsx';
import { WorkspaceContainer } from '../components/WorkspaceLayout.tsx';
import { useMe } from '../lib/auth.ts';
import { useSettings, useUpdateSettings, useUploadSignature } from '../lib/settings.ts';
import type { SettingsResponse } from '../../shared/settings.ts';

export const Route = createFileRoute('/settings')({
  component: SettingsRoute,
});

interface SettingsFormState {
  senderName: string;
  nextInvoiceNumber: string;
  notesTemplateEur: string;
  notesTemplateUsd: string;
  notesTemplateRub: string;
  bankRequisitesEur: string;
  bankRequisitesUsd: string;
  bankRequisitesRub: string;
}

const BANK_REQUISITES_PLACEHOLDERS = {
  eur: 'Beneficiary:\nBank:\nBank address:\nIBAN:\nSWIFT/BIC:',
  usd: 'Beneficiary:\nBank:\nBank address:\nAccount no.:\nSWIFT/BIC:\nIntermediary bank:\nIntermediary SWIFT:',
  rub: 'Получатель:\nИНН:\nКПП:\nР/с:\nБанк:\nБИК:\nК/с:',
} as const;

const NEXT_INVOICE_NUMBER_ERROR = 'Next invoice number must be 1 or higher.';
const LOAD_SETTINGS_ERROR = 'Could not load settings. Try again or check the logs.';
const SAVE_SETTINGS_ERROR = 'Could not save settings. Try again or check the logs.';
const UPLOAD_SIGNATURE_ERROR = 'Could not upload signature. Try again or check the logs.';
const NEXT_INVOICE_NUMBER_PATTERN = /^[1-9]\d*$/;
const SAFE_SIGNATURE_UPLOAD_ERRORS = new Set([
  'Invalid SVG file.',
  'SVG file is too large. Max 100 KB.',
]);
export function SettingsRoute(): React.ReactElement {
  const me = useMe();

  if (me.isLoading) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  if (me.data == null) {
    return <LoginScreen />;
  }

  return (
    <AuthenticatedMobileShell currentPath="/settings">
      <AuthenticatedSettings />
    </AuthenticatedMobileShell>
  );
}

function AuthenticatedSettings(): React.ReactElement {
  const settingsQuery = useSettings();

  if (settingsQuery.isPending) {
    return (
      <SettingsShell saveButton={<SaveButton disabled />}>
        <p className="text-sm text-ink-muted">Loading…</p>
      </SettingsShell>
    );
  }

  if (settingsQuery.isError) {
    return (
      <SettingsShell saveButton={<SaveButton disabled />}>
        <StatusMessage tone="error">{LOAD_SETTINGS_ERROR}</StatusMessage>
      </SettingsShell>
    );
  }

  return <SettingsFormPage data={settingsQuery.data} />;
}

function SettingsFormPage({ data }: { data: SettingsResponse }): React.ReactElement {
  const [form, setForm] = useState<SettingsFormState>(() => toFormState(data));
  const previewUrl = data.senderProfile.signaturePreviewUrl;
  const saveState = useSettingsSaveState(form);
  const signatureState = useSignatureUploadState(data.senderProfile.id);

  return (
    <form id="settings-form" aria-label="Settings form" noValidate onSubmit={saveState.onSubmit}>
      <SettingsShell saveButton={<SaveButton type="submit" disabled={saveState.saving} />}>
        <div className="space-y-6">
          <SettingsStatusArea
            isSaving={saveState.saving}
            saveStatus={saveState.status}
            saveError={saveState.genericError}
          />
          <SettingsUpperGrid
            form={form}
            setForm={setForm}
            saveError={saveState.error}
            previewUrl={previewUrl}
            signatureUploading={signatureState.uploading}
            uploadStatus={signatureState.status}
            uploadError={signatureState.error}
            onSignatureChange={signatureState.onChange}
          />
          <CurrencyTemplatesSection form={form} setForm={setForm} />
        </div>
      </SettingsShell>
    </form>
  );
}

function useSettingsSaveState(form: SettingsFormState): {
  saving: boolean;
  status: string | null;
  error: string | null;
  genericError: string | null;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
} {
  const updateSettings = useUpdateSettings();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const status = useMemo(
    () => (updateSettings.isPending ? 'Saving…' : message),
    [message, updateSettings.isPending],
  );
  const genericError = error && error !== NEXT_INVOICE_NUMBER_ERROR ? error : null;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setMessage(null);
    setError(null);
    if (!NEXT_INVOICE_NUMBER_PATTERN.test(form.nextInvoiceNumber)) {
      setError(NEXT_INVOICE_NUMBER_ERROR);
      return;
    }
    try {
      await updateSettings.mutateAsync(toUpdateSettingsInput(form));
      setMessage('Settings updated.');
    } catch {
      setError(SAVE_SETTINGS_ERROR);
    }
  }

  return { saving: updateSettings.isPending, status, error, genericError, onSubmit };
}

function useSignatureUploadState(senderProfileId: string | null): {
  uploading: boolean;
  status: string | null;
  error: string | null;
  onChange: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
} {
  const uploadSignature = useUploadSignature();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const status = useMemo(
    () => (uploadSignature.isPending ? 'Uploading…' : message),
    [message, uploadSignature.isPending],
  );

  async function onChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file || !senderProfileId) return;
    setMessage(null);
    setError(null);
    try {
      await uploadSignature.mutateAsync({ senderProfileId, file });
      setMessage('Signature updated.');
    } catch (caught) {
      setError(toSafeSignatureUploadError(caught));
    }
  }

  return { uploading: uploadSignature.isPending, status, error, onChange };
}

function toSafeSignatureUploadError(caught: unknown): string {
  if (caught instanceof Error && SAFE_SIGNATURE_UPLOAD_ERRORS.has(caught.message)) {
    return caught.message;
  }
  return UPLOAD_SIGNATURE_ERROR;
}

function SettingsShell({
  children,
  saveButton,
}: {
  children: React.ReactNode;
  saveButton: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="min-h-screen">
      <WorkspaceContainer as="main" className="py-6 sm:py-8">
        <div className="space-y-6">
          <SettingsPageHeader saveButton={saveButton} />
          {children}
        </div>
      </WorkspaceContainer>
    </div>
  );
}

function SettingsPageHeader({ saveButton }: { saveButton: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex flex-col gap-4 border-b border-line pb-6 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <Link
          to="/"
          aria-label="Back"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line bg-paper text-xl leading-none text-ink-soft transition hover:border-ochre/40 hover:bg-paper-deep"
        >
          ‹
        </Link>
        <h1 className="truncate font-serif text-3xl italic text-ink sm:text-4xl">Settings</h1>
      </div>
      {saveButton}
    </div>
  );
}

function SettingsStatusArea({
  isSaving,
  saveStatus,
  saveError,
}: {
  isSaving: boolean;
  saveStatus: string | null;
  saveError: string | null;
}): React.ReactElement | null {
  if (!saveStatus && !saveError) return null;

  return (
    <div className="space-y-3">
      {saveStatus ? (
        <StatusMessage tone={isSaving ? 'pending' : 'success'}>{saveStatus}</StatusMessage>
      ) : null}
      {saveError ? <StatusMessage tone="error">{saveError}</StatusMessage> : null}
    </div>
  );
}

function SettingsUpperGrid({
  form,
  setForm,
  saveError,
  previewUrl,
  signatureUploading,
  uploadStatus,
  uploadError,
  onSignatureChange,
}: {
  form: SettingsFormState;
  setForm: React.Dispatch<React.SetStateAction<SettingsFormState>>;
  saveError: string | null;
  previewUrl: string | null;
  signatureUploading: boolean;
  uploadStatus: string | null;
  uploadError: string | null;
  onSignatureChange: (event: ChangeEvent<HTMLInputElement>) => void;
}): React.ReactElement {
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(16rem,0.85fr)]">
      <SenderProfileSection
        form={form}
        setForm={setForm}
        previewUrl={previewUrl}
        signatureUploading={signatureUploading}
        uploadStatus={uploadStatus}
        uploadError={uploadError}
        onSignatureChange={onSignatureChange}
      />
      <InvoiceNumberingSection form={form} setForm={setForm} saveError={saveError} />
    </div>
  );
}

function SenderProfileSection({
  form,
  setForm,
  previewUrl,
  signatureUploading,
  uploadStatus,
  uploadError,
  onSignatureChange,
}: {
  form: SettingsFormState;
  setForm: React.Dispatch<React.SetStateAction<SettingsFormState>>;
  previewUrl: string | null;
  signatureUploading: boolean;
  uploadStatus: string | null;
  uploadError: string | null;
  onSignatureChange: (event: ChangeEvent<HTMLInputElement>) => void;
}): React.ReactElement {
  return (
    <section aria-labelledby="settings-sender-heading" className="min-w-0 space-y-4">
      <h2
        id="settings-sender-heading"
        className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-muted"
      >
        Sender profile
      </h2>
      <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(14rem,0.75fr)]">
        <TextField
          id="sender-name"
          label="Sender name"
          value={form.senderName}
          onChange={(event) => updateField('senderName', event.currentTarget.value, setForm)}
        />
        <SignatureUploadField
          previewUrl={previewUrl}
          signatureUploading={signatureUploading}
          uploadStatus={uploadStatus}
          uploadError={uploadError}
          onSignatureChange={onSignatureChange}
        />
      </div>
    </section>
  );
}

function SignatureUploadField({
  previewUrl,
  signatureUploading,
  uploadStatus,
  uploadError,
  onSignatureChange,
}: {
  previewUrl: string | null;
  signatureUploading: boolean;
  uploadStatus: string | null;
  uploadError: string | null;
  onSignatureChange: (event: ChangeEvent<HTMLInputElement>) => void;
}): React.ReactElement {
  return (
    <div className="min-w-0 space-y-3">
      <label
        className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-muted"
        htmlFor="signature-upload"
      >
        Replace signature
      </label>
      {previewUrl ? (
        <img
          src={previewUrl}
          alt="Signature preview"
          className="max-h-28 max-w-full rounded-xl border border-line bg-paper-soft p-3"
        />
      ) : null}
      <input
        id="signature-upload"
        type="file"
        accept="image/svg+xml,.svg"
        disabled={signatureUploading}
        onChange={onSignatureChange}
        className="block min-h-11 w-full max-w-full min-w-0 text-sm text-ink-soft file:mr-3 file:min-h-11 file:rounded-full file:border-0 file:bg-ink file:px-4 file:py-2 file:text-sm file:font-semibold file:text-paper-soft hover:file:bg-ink/85 disabled:cursor-not-allowed disabled:opacity-60"
      />
      {uploadStatus ? (
        <StatusMessage tone={signatureUploading ? 'pending' : 'success'}>
          {uploadStatus}
        </StatusMessage>
      ) : null}
      {uploadError ? <StatusMessage tone="error">{uploadError}</StatusMessage> : null}
    </div>
  );
}

function InvoiceNumberingSection({
  form,
  setForm,
  saveError,
}: {
  form: SettingsFormState;
  setForm: React.Dispatch<React.SetStateAction<SettingsFormState>>;
  saveError: string | null;
}): React.ReactElement {
  return (
    <section aria-labelledby="settings-numbering-heading" className="min-w-0 space-y-4">
      <h2
        id="settings-numbering-heading"
        className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-muted"
      >
        Invoice numbering
      </h2>
      <NumberField
        id="next-invoice-number"
        label="Next invoice number"
        min={1}
        step={1}
        value={form.nextInvoiceNumber}
        errorText={saveError === NEXT_INVOICE_NUMBER_ERROR ? saveError : undefined}
        onChange={(event) => updateField('nextInvoiceNumber', event.currentTarget.value, setForm)}
      />
    </section>
  );
}

const TEMPLATE_CURRENCIES = [
  {
    code: 'EUR',
    notesField: 'notesTemplateEur',
    requisitesField: 'bankRequisitesEur',
    requisitesPlaceholder: BANK_REQUISITES_PLACEHOLDERS.eur,
  },
  {
    code: 'USD',
    notesField: 'notesTemplateUsd',
    requisitesField: 'bankRequisitesUsd',
    requisitesPlaceholder: BANK_REQUISITES_PLACEHOLDERS.usd,
  },
  {
    code: 'RUB',
    notesField: 'notesTemplateRub',
    requisitesField: 'bankRequisitesRub',
    requisitesPlaceholder: BANK_REQUISITES_PLACEHOLDERS.rub,
  },
] as const;

function CurrencyTemplatesSection({
  form,
  setForm,
}: {
  form: SettingsFormState;
  setForm: React.Dispatch<React.SetStateAction<SettingsFormState>>;
}): React.ReactElement {
  return (
    <section aria-labelledby="settings-templates-heading" className="min-w-0 space-y-4">
      <h2
        id="settings-templates-heading"
        className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-muted"
      >
        Per-currency templates
      </h2>
      <p className="text-sm text-ink-muted">
        Notes seed the invoice editor when its currency is selected. Bank requisites print inside
        the PDF sender column when the matching field is non-empty.
      </p>
      <div className="grid min-w-0 gap-6">
        {TEMPLATE_CURRENCIES.map((currency) => (
          <CurrencyTemplateRow
            key={currency.code}
            code={currency.code}
            notesValue={form[currency.notesField]}
            requisitesValue={form[currency.requisitesField]}
            requisitesPlaceholder={currency.requisitesPlaceholder}
            onNotesChange={(value) => updateField(currency.notesField, value, setForm)}
            onRequisitesChange={(value) => updateField(currency.requisitesField, value, setForm)}
          />
        ))}
      </div>
    </section>
  );
}

function CurrencyTemplateRow({
  code,
  notesValue,
  requisitesValue,
  requisitesPlaceholder,
  onNotesChange,
  onRequisitesChange,
}: {
  code: 'EUR' | 'USD' | 'RUB';
  notesValue: string;
  requisitesValue: string;
  requisitesPlaceholder: string;
  onNotesChange: (value: string) => void;
  onRequisitesChange: (value: string) => void;
}): React.ReactElement {
  const slug = code.toLowerCase();
  return (
    <div
      aria-labelledby={`settings-templates-${slug}-heading`}
      className="min-w-0 space-y-3 rounded-2xl border border-line bg-paper-soft/40 p-4 sm:p-5"
      role="group"
    >
      <h3
        id={`settings-templates-${slug}-heading`}
        className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink"
      >
        {code}
      </h3>
      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <TextArea
          id={`notes-${slug}`}
          label="Notes"
          aria-label={`${code} notes`}
          rows={7}
          value={notesValue}
          className="lg:min-h-44"
          onChange={(event) => onNotesChange(event.currentTarget.value)}
        />
        <TextArea
          id={`bank-requisites-${slug}`}
          label="Bank requisites"
          aria-label={`${code} bank requisites`}
          rows={7}
          value={requisitesValue}
          placeholder={requisitesPlaceholder}
          className="lg:min-h-44"
          onChange={(event) => onRequisitesChange(event.currentTarget.value)}
        />
      </div>
    </div>
  );
}

function SaveButton({
  disabled,
  onClick,
  type = 'button',
}: {
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
}): React.ReactElement {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-full bg-ochre px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep disabled:cursor-not-allowed disabled:opacity-60"
    >
      Save Settings
    </button>
  );
}

function toFormState(data: SettingsResponse): SettingsFormState {
  return {
    senderName: data.senderProfile.name,
    nextInvoiceNumber: String(data.settings.nextInvoiceNumber),
    notesTemplateEur: data.settings.notesTemplateEur,
    notesTemplateUsd: data.settings.notesTemplateUsd,
    notesTemplateRub: data.settings.notesTemplateRub,
    bankRequisitesEur: data.senderProfile.bankRequisitesEur,
    bankRequisitesUsd: data.senderProfile.bankRequisitesUsd,
    bankRequisitesRub: data.senderProfile.bankRequisitesRub,
  };
}

function toUpdateSettingsInput(form: SettingsFormState): {
  senderName: string;
  nextInvoiceNumber: number;
  notesTemplateEur: string;
  notesTemplateUsd: string;
  notesTemplateRub: string;
  bankRequisitesEur: string;
  bankRequisitesUsd: string;
  bankRequisitesRub: string;
} {
  return {
    senderName: form.senderName,
    nextInvoiceNumber: Number(form.nextInvoiceNumber),
    notesTemplateEur: form.notesTemplateEur,
    notesTemplateUsd: form.notesTemplateUsd,
    notesTemplateRub: form.notesTemplateRub,
    bankRequisitesEur: form.bankRequisitesEur,
    bankRequisitesUsd: form.bankRequisitesUsd,
    bankRequisitesRub: form.bankRequisitesRub,
  };
}

function updateField(
  field: keyof SettingsFormState,
  value: string,
  setForm: React.Dispatch<React.SetStateAction<SettingsFormState>>,
): void {
  setForm((current) => ({ ...current, [field]: value }));
}

function LoginScreen(): React.ReactElement {
  return (
    <CenteredFrame>
      <div className="flex flex-col items-center gap-6 rounded-2xl border border-line bg-paper-soft px-10 py-12 shadow-[0_18px_60px_-30px_rgba(31,27,21,0.35)]">
        <div className="flex flex-col items-center gap-2">
          <h1 className="font-serif text-4xl font-medium italic leading-tight text-ink">
            Invoice Generator
          </h1>
          <p className="max-w-xs text-balance text-sm text-ink-soft">
            A small, private workshop for your invoices. Sign in to continue.
          </p>
        </div>
        <a
          href="/auth/github/login"
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-ochre px-6 py-2.5 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-10px_rgba(176,119,43,0.8)] transition hover:bg-ochre-deep"
        >
          Sign in with GitHub
        </a>
      </div>
    </CenteredFrame>
  );
}

function CenteredFrame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="flex flex-col items-center gap-4 text-center">{children}</div>
    </div>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center px-6 text-sm text-ink-muted">
      {children}
    </div>
  );
}
